/**
 * Campaign Service - UX Overhaul Sprint 1
 * Wire CampaignStatusCards to real API
 * 
 * Story HW-6: Campaign Status Cards shows real campaigns from /api/v1/campaigns
 * Story HW-2: New Campaign quick action launches 12-step workflow
 * NO MOCK DATA - Real API calls only
 * 
 * Canonical API client usage (explicit backend paths)
 * - No implicit path prefixing
 * - No .data wrapper (returns data directly)
 */

import { apiFetch } from '@/lib/api';

// Types matching backend models
export interface Campaign {
  id: string;
  name: string;
  description?: string;
  framework?: 'PAS' | 'AIDA' | 'BAB' | 'STAR';
  client_id?: string;
  primary_partner_id?: string;
  target_industries?: string[];
  icp_summary?: string;
  ai_config?: {
    temperature: number;
    model: string;
    customPrompts: boolean;
  };
  status: 'DRAFT' | 'SCHEDULED' | 'RUNNING' | 'PAUSED' | 'COMPLETED' | 'EXPORTED' | 'CANCELLED' | 'STEP_11_DELIVERY' | 'GENERATING_CONTENT' | 'STEP_9_QC';
  progress: number;
  current_phase?: string;
  content_pieces?: number;
  target_content_pieces?: number;
  created_by?: string;
  organization_id?: string;
  created_at: string;
  updated_at: string;
  workflow_id?: string;
  next_action?: string;
  
  // Epic 31 Intelligence Fields
  campaign_type?: 'abm' | 'cosell' | 'demand_gen' | 'pipeline_acceleration' | 'customer_expansion';
  journey_stages?: string[];
  success_metrics?: string[];
  workflow_template?: string;
  
  // Schema completeness (from backend models)
  solution_id?: string;  // Primary solution for campaign
  content_modifiers?: Record<string, any>;  // Campaign-specific content modifications
  
  // Related data
  partners?: Array<{ partner_id: string; role: string }>;
  solutions?: Array<{ solution_id: string; priority: number }>;
}

export interface CampaignListResponse {
  campaigns: Campaign[];
  total: number;
  page: number;
  page_size: number;
}

export interface CampaignCreateRequest {
  name: string;
  client_id?: string;
  description?: string;
  target_audience?: string;
}

export interface CampaignStatusResponse {
  id: string;
  status: Campaign['status'];
  progress: number;
  current_step: number;
  total_steps: number;
  last_activity: string;
}

class CampaignService {
  /**
   * Get all campaigns for CampaignStatusCards
   * Story HW-6: Shows real campaigns from /api/v1/campaigns or empty state
   */
  async getCampaigns(page = 1, pageSize = 10): Promise<CampaignListResponse> {
    const campaigns = await apiFetch<Campaign[]>(
      `/api/v1/campaigns?page=${page}&page_size=${pageSize}`
    );
    
    // Backend returns array, wrap in response object
    return {
      campaigns: campaigns || [],
      total: campaigns?.length || 0,
      page,
      page_size: pageSize
    };
  }

  /**
   * Get single campaign by ID
   * Uses canonical campaign setup endpoint
   */
  async getCampaign(campaignId: string): Promise<Campaign> {
    return await apiFetch<Campaign>(`/api/v1/campaigns/${campaignId}/setup`);
  }

  /**
   * Create new campaign
   * Story HW-2: New Campaign quick action
   * Uses canonical campaign create endpoint
   */
  async createCampaign(data: CampaignCreateRequest): Promise<Campaign> {
    if (!data.client_id) {
      throw new Error('client_id is required to create a campaign');
    }

    return await apiFetch<Campaign>('/api/v1/campaigns', {
      method: 'POST',
      body: JSON.stringify({
        client_id: data.client_id,
        client_name: data.name,
      })
    });
  }

  /**
   * Update campaign
   * Uses canonical campaign update endpoint
   */
  async updateCampaign(campaignId: string, data: Partial<CampaignCreateRequest>): Promise<Campaign> {
    void campaignId;
    void data;
    throw new Error('Generic campaign update endpoint is not supported. Use setup endpoints for Step 4 updates.');
  }

  /**
   * Delete campaign
   * Uses canonical campaign delete endpoint
   */
  async deleteCampaign(campaignId: string): Promise<void> {
    await apiFetch(`/api/v1/campaigns/${campaignId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Get campaign status
   * Uses canonical campaign status endpoint
   */
  async getCampaignStatus(campaignId: string): Promise<CampaignStatusResponse> {
    return await apiFetch<CampaignStatusResponse>(
      `/api/v1/campaigns/${campaignId}/generation/status`
    );
  }

  /**
   * Check campaign readiness for AI generation
   * Uses canonical campaign validation endpoint
   */
  async checkReadiness(campaignId: string): Promise<{
    ready: boolean;
    missing_requirements: string[];
    fuel_level: number;
  }> {
    const result = await apiFetch<{
      valid?: boolean;
      issues?: string[];
      ready_to_export?: boolean;
    }>(`/api/v1/campaigns/${campaignId}/validate`, { method: 'POST' });

    return {
      ready: Boolean(result?.valid ?? result?.ready_to_export),
      missing_requirements: result?.issues ?? [],
      fuel_level: (result?.valid ?? result?.ready_to_export) ? 100 : 0,
    };
  }
}

export const campaignService = new CampaignService();
export default campaignService;
